'use client';

// 2026-07 — WIDGET GALERIE VIDEO (« capsules » façon YouTube).
//
// Sources supportées :
//   - Fichiers auto-hébergés (FTP Ligobox) : MP4 + WebM (balise <video>)
//   - YouTube (youtube.com/watch, youtu.be, /shorts/) — embed no-cookie
//   - Vimeo — player.vimeo.com
//   - Dailymotion (dailymotion.com/video, dai.ly)
//
// DEUX MODES (2026-07, mode « watch » façon YouTube au lieu du modal) :
//   - GRILLE : vignettes (durée, titre, vues, likes)
//   - WATCH  : au clic, le lecteur s'affiche EN GRAND À GAUCHE et reste
//     VISIBLE AU SCROLL (sticky) ; les autres capsules défilent À DROITE.
//     Sur MOBILE : lecteur collé en haut de l'écran (sticky top), titre,
//     actions et liste des autres vidéos défilent en dessous.
//
// Fonctionnalités :
//   - Players chargés AU CLIC uniquement (perf + zéro cookie tiers avant
//     lecture — RGPD friendly)
//   - Vues (1/session/vidéo) + likes (1/visiteur, localStorage) stockés en
//     base via /api/vitrine/videos/*
//   - Partage : Facebook, X, LinkedIn, WhatsApp, Telegram, Pinterest, Email,
//     Copier le lien — deep-link ?video=<id> qui rouvre la bonne vidéo
//   - JSON-LD VideoObject par vidéo (indexation Google Vidéos)
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.ligobox.com';

// Detection de source, vignette et integration : voir lib/videoSource.js.
// Le widget « Image + texte » lit les memes regles depuis ce module.
import { parseVideoSource, autoThumbnail, embedUrl } from '@/lib/videoSource';
export { parseVideoSource, autoThumbnail, embedUrl };

function formatCount(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace('.0', '')} M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace('.0', '')} k`;
  return String(n);
}

// Slug LISIBLE pour l'URL de partage (?video=...) : dérivé du TITRE de la
// vidéo (jamais de l'id interne type « demo-youtube-1 »).
function videoUrlSlug(v, idx) {
  const base = String(v.title || v.id || `video-${idx + 1}`)
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return base || `video-${idx + 1}`;
}

function shareTargets(url, title) {
  const eu = encodeURIComponent(url);
  const et = encodeURIComponent(title || 'Vidéo Ligobox');
  return [
    { name: 'Facebook',  href: `https://www.facebook.com/sharer/sharer.php?u=${eu}` },
    { name: 'X',         href: `https://twitter.com/intent/tweet?url=${eu}&text=${et}` },
    { name: 'LinkedIn',  href: `https://www.linkedin.com/sharing/share-offsite/?url=${eu}` },
    { name: 'WhatsApp',  href: `https://wa.me/?text=${et}%20${eu}` },
    { name: 'Telegram',  href: `https://t.me/share/url?url=${eu}&text=${et}` },
    { name: 'Pinterest', href: `https://pinterest.com/pin/create/button/?url=${eu}&description=${et}` },
    { name: 'Email',     href: `mailto:?subject=${et}&body=${eu}` },
  ];
}

// CSS responsive du mode watch (media queries impossibles en inline styles).
// Desktop : 2 colonnes, lecteur sticky sous le header vitrine.
// Mobile : 1 colonne, LECTEUR COLLE EN HAUT de l'écran pendant le scroll.
const WATCH_CSS = `
.lgbx-watch { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 28px; align-items: start; }
.lgbx-watch-left { position: sticky; top: 84px; }
.lgbx-watch-player { position: relative; aspect-ratio: 16 / 9; background: #000; border-radius: 0; overflow: hidden; }
/* Mode "Agrandir" (theatre) : le lecteur prend toute la largeur de la page,
   la liste "A regarder ensuite" passe en dessous. */
.lgbx-watch--theater { grid-template-columns: 1fr; }
.lgbx-watch--theater .lgbx-watch-left { position: static; }
/* Plein ecran natif (toute la surface du moniteur) : on neutralise le ratio
   pour que le lecteur remplisse l'ecran. */
.lgbx-watch-player:fullscreen { aspect-ratio: auto; width: 100%; height: 100%; }
.lgbx-watch-player:-webkit-full-screen { aspect-ratio: auto; width: 100%; height: 100%; }
/* Cartes de la grille : halo d'arriere-plan au survol, facon YouTube,
   en ORANGE Ligobox (#e27228) avec opacite. Le fallback hex-alpha couvre les
   vieux navigateurs, color-mix suit la couleur primaire du theme. */
.lgbx-card { background: transparent; border-radius: 0; padding: 8px; transition: background .15s ease; align-self: start; }
.lgbx-card:hover { background: #e2722826 !important; background: color-mix(in srgb, var(--color-primary, #e27228) 15%, transparent) !important; }
@media (max-width: 960px) {
  .lgbx-watch { grid-template-columns: 1fr; gap: 18px; }
  .lgbx-watch-left { position: static; }
  .lgbx-watch-player { position: sticky; top: 0; z-index: 60; border-radius: 0; margin: 0 -16px; }
}
`;

export default function VideoGalleryBlock({ config = {} }) {
  const videos = useMemo(
    () => (Array.isArray(config.videos) ? config.videos.filter(v => v && v.url) : []),
    [config.videos]
  );
  const columns = [2, 3, 4].includes(config.columns) ? config.columns : 3;
  const [stats, setStats] = useState({});
  const [openId, setOpenId] = useState(null);
  const [liked, setLiked] = useState({});
  const [copied, setCopied] = useState(false);
  // Vrai = le visiteur a cliqué sur la vidéo : l'interface YouTube est visible.
  // Faux (défaut) = lecture « propre » : un bouclier transparent bloque le
  // survol, donc AUCUN bandeau d'infos YouTube pendant la lecture.
  const [uiRevealed, setUiRevealed] = useState(false);
  const [theater, setTheater] = useState(false);
  // Ratio RÉEL de la vidéo fichier (MP4/WebM) lu à la volée : le lecteur
  // adopte ce ratio → AUCUNE bande noire et AUCUNE coupure, quel que soit
  // le format d'enregistrement de la capsule.
  const [filePlayerRatio, setFilePlayerRatio] = useState(null);
  // Ratio réel des EMBEDS (YouTube/Vimeo/Dailymotion) via l'API oEmbed
  // (proxifiée par le backend) : cache url → "w / h". Le cadre du lecteur
  // adopte ce ratio → l'embed remplit tout, plus de bandes noires internes.
  const [embedRatios, setEmbedRatios] = useState({});
  const blockRef = useRef(null);
  const playerWrapRef = useRef(null);

  // Stats en lot + likes déjà donnés par ce visiteur
  useEffect(() => {
    if (!videos.length) return;
    const ids = videos.map(v => v.id).filter(Boolean);
    fetch(`${API_URL}/api/vitrine/videos/stats?ids=${ids.join(',')}`, { cache: 'no-store' })
      .then(r => r.json())
      .then(d => setStats(d?.stats || {}))
      .catch(() => {});
    try {
      setLiked(JSON.parse(localStorage.getItem('ligobox_video_likes') || '{}'));
    } catch (_) {}
  }, [videos]);

  // Slugs d'URL LISIBLES (dérivés des titres) — dédupliqués si titres égaux.
  const urlSlugs = useMemo(() => {
    const used = new Set();
    const map = {}; // id -> slug
    videos.forEach((v, idx) => {
      let s = videoUrlSlug(v, idx);
      let n = 2;
      while (used.has(s)) { s = `${videoUrlSlug(v, idx)}-${n}`; n += 1; }
      used.add(s);
      map[v.id] = s;
    });
    return map;
  }, [videos]);

  // Deep-link ?video=<slug> → ouvre directement la vidéo (liens partagés).
  // Compat : accepte aussi l'ancien format par id interne.
  useEffect(() => {
    try {
      const param = new URLSearchParams(window.location.search).get('video');
      if (!param) return;
      const target = videos.find(v => urlSlugs[v.id] === param || v.id === param);
      if (target) setOpenId(target.id);
    } catch (_) {}
  }, [videos, urlSlugs]);

  const registerView = useCallback((vid) => {
    try {
      const key = `ligobox_video_viewed_${vid}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (_) {}
    fetch(`${API_URL}/api/vitrine/videos/${vid}/view`, { method: 'POST' })
      .then(r => r.json())
      .then(d => setStats(s => ({ ...s, [vid]: { ...(s[vid] || {}), views: d.views } })))
      .catch(() => {});
  }, []);

  // Charge le ratio réel de la vidéo EMBED ouverte (1 seul appel par URL)
  useEffect(() => {
    const cur = videos.find(v => v.id === openId);
    if (!cur) return;
    const src = parseVideoSource(cur.url);
    if (src.kind === 'file' || embedRatios[cur.url] !== undefined) return;
    fetch(`${API_URL}/api/vitrine/videos/embed-ratio?url=${encodeURIComponent(cur.url)}`)
      .then(r => r.json())
      .then(d => {
        setEmbedRatios(prev => ({
          ...prev,
          [cur.url]: (d && d.width && d.height) ? `${d.width} / ${d.height}` : null,
        }));
      })
      .catch(() => setEmbedRatios(prev => ({ ...prev, [cur.url]: null })));
  }, [openId, videos, embedRatios]);

  const openVideo = (vid) => {
    setUiRevealed(false); // nouvelle vidéo : lecture « propre » sans bandeau
    setOpenId(vid);
    setCopied(false);
    setFilePlayerRatio(null); // ratio recalculé pour la nouvelle vidéo
    registerView(vid);
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('video', urlSlugs[vid] || vid);
      window.history.replaceState(null, '', u.toString());
    } catch (_) {}
    // Remonte en haut du bloc pour voir le lecteur (surtout au switch de
    // video depuis la liste de droite, quand on a deja scrolle).
    try {
      if (blockRef.current) {
        const y = blockRef.current.getBoundingClientRect().top + window.scrollY - 70;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    } catch (_) {}
  };

  // Plein ecran NATIF du navigateur : la video occupe toute la surface du
  // moniteur (touche Echap ou le meme bouton pour sortir).
  const goFullscreen = () => {
    const el = playerWrapRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } else if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
    } catch (_) {}
  };

  const closeVideo = () => {
    setOpenId(null);
    setTheater(false);
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('video');
      window.history.replaceState(null, '', u.toString());
    } catch (_) {}
  };

  const toggleLike = (vid) => {
    const isLiked = !!liked[vid];
    const next = { ...liked, [vid]: !isLiked };
    if (isLiked) delete next[vid];
    setLiked(next);
    try { localStorage.setItem('ligobox_video_likes', JSON.stringify(next)); } catch (_) {}
    fetch(`${API_URL}/api/vitrine/videos/${vid}/like`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ unlike: isLiked }),
    })
      .then(r => r.json())
      .then(d => setStats(s => ({ ...s, [vid]: { ...(s[vid] || {}), likes: d.likes } })))
      .catch(() => {});
  };

  if (!videos.length) return null;
  const current = videos.find(v => v.id === openId) || null;
  const currentSource = current ? parseVideoSource(current.url) : null;
  const others = current ? videos.filter(v => v.id !== current.id) : [];
  const shareUrl = (() => {
    try {
      const u = new URL(window.location.href);
      if (current) u.searchParams.set('video', urlSlugs[current.id] || current.id);
      return u.toString();
    } catch (_) { return ''; }
  })();

  // JSON-LD VideoObject — indexation Google Vidéos (déterministe : config only)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: videos.map((v, i) => {
      const src = parseVideoSource(v.url);
      return {
        '@type': 'VideoObject',
        position: i + 1,
        name: v.title || `Vidéo ${i + 1}`,
        description: v.description || v.title || '',
        thumbnailUrl: autoThumbnail(src, v) || undefined,
        contentUrl: src.kind === 'file' ? v.url : undefined,
        embedUrl: embedUrl(src) || undefined,
        uploadDate: v.published_at || undefined,
      };
    }),
  };

  const statLine = (vid) => {
    const st = stats[vid] || {};
    return `${formatCount(st.views || 0)} vue${(st.views || 0) > 1 ? 's' : ''} · ♥ ${formatCount(st.likes || 0)}`;
  };

  return (
    <section ref={blockRef} style={{ padding: 'calc(var(--section-py, 100px) * 0.6) 0' }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <style dangerouslySetInnerHTML={{ __html: WATCH_CSS }} />
      <div className="container-page">
        {(config.title || config.subtitle) && !current && (
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            {config.title && (
              <h2 style={{ margin: '0 0 10px', color: 'var(--color-text)' }}>{config.title}</h2>
            )}
            {config.subtitle && (
              <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 17 }}>{config.subtitle}</p>
            )}
          </div>
        )}

        {/* ══ MODE WATCH : lecteur a gauche (sticky), autres capsules a droite ══ */}
        {current ? (
          <div>
            <button
              type="button"
              onClick={closeVideo}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                marginBottom: 16, padding: '8px 16px', borderRadius: 999,
                border: '1px solid var(--color-border, #e2e8f0)',
                background: 'var(--color-surface, #fff)', color: 'var(--color-text)',
                fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >← Toutes les vidéos</button>

            <div className={'lgbx-watch' + (theater ? ' lgbx-watch--theater' : '')}>
              {/* ── Colonne gauche : lecteur STICKY + infos ── */}
              <div className="lgbx-watch-left">
                <div className="lgbx-watch-player" ref={playerWrapRef}
                  style={
                    currentSource.kind === 'file'
                      ? (filePlayerRatio ? { aspectRatio: String(filePlayerRatio) } : undefined)
                      : (embedRatios[current.url] ? { aspectRatio: embedRatios[current.url] } : undefined)
                  }>
                  {currentSource.kind === 'file' ? (
                    /* Le lecteur ADOPTE le format réel du fichier (métadonnées) :
                       image entière bord à bord, AUCUNE bande, AUCUNE coupure —
                       quel que soit le format d'enregistrement (16:9, 1,86:1...). */
                    <video key={current.id} controls autoPlay playsInline
                      onLoadedMetadata={(e) => {
                        const w = e.currentTarget.videoWidth, h = e.currentTarget.videoHeight;
                        if (w > 0 && h > 0) setFilePlayerRatio(`${w} / ${h}`);
                      }}
                      style={{ width: '100%', height: '100%', display: 'block' }}
                      poster={current.thumbnail_url || undefined}>
                      {current.url_webm && <source src={current.url_webm} type="video/webm" />}
                      <source src={current.url} type={/\.webm(\?.*)?$/i.test(current.url) ? 'video/webm' : 'video/mp4'} />
                      Votre navigateur ne supporte pas la lecture vidéo.
                    </video>
                  ) : (
                    /* QUALITÉ (2026-07) : le lecteur YouTube choisit sa qualité
                       à la taille du cadre AU CHARGEMENT. On ne monte donc
                       l'iframe qu'une fois le format réel connu (cadre à sa
                       taille DÉFINITIVE) — sinon le redimensionnement après
                       coup laissait YouTube bloqué en basse qualité (flou). */
                    embedRatios[current.url] === undefined ? (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>
                        Chargement de la vidéo…
                      </div>
                    ) : (
                    <>
                    <iframe
                      key={current.id}
                      src={embedUrl(currentSource)}
                      title={current.title || 'Vidéo'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                      style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                    />
                    {!uiRevealed && (
                      /* Bouclier transparent : bloque le survol pour que YouTube
                         n'affiche pas son bandeau pendant la lecture. Un clic
                         sur la vidéo le retire et rend l'interface YouTube. */
                      <div
                        onClick={() => setUiRevealed(true)}
                        aria-label="Afficher les contrôles de la vidéo"
                        style={{ position: 'absolute', inset: 0, cursor: 'pointer', background: 'transparent' }}
                      />
                    )}
                    </>
                    )
                  )}
                </div>

                <h3 style={{ margin: '14px 0 6px', fontSize: 19, color: 'var(--color-text)', lineHeight: 1.35 }}>
                  {current.title || 'Sans titre'}
                </h3>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 10,
                }}>
                  <span>{formatCount((stats[current.id] || {}).views || 0)} vue{((stats[current.id] || {}).views || 0) > 1 ? 's' : ''}</span>
                  <button
                    type="button"
                    onClick={() => toggleLike(current.id)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      border: '1px solid ' + (liked[current.id] ? 'var(--color-primary, #e27228)' : 'var(--color-border, #e2e8f0)'),
                      background: liked[current.id] ? 'var(--color-primary, #e27228)' : 'transparent',
                      color: liked[current.id] ? '#fff' : 'var(--color-text)',
                      fontFamily: 'inherit',
                    }}
                  >
                    ♥ {formatCount((stats[current.id] || {}).likes || 0)}{liked[current.id] ? ' · Aimé' : " · J'aime"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      try { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {}
                    }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      border: '1px solid var(--color-border, #e2e8f0)', background: 'transparent',
                      color: 'var(--color-text)', fontFamily: 'inherit',
                    }}
                  >{copied ? '✓ Lien copié' : '🔗 Copier le lien'}</button>
                  <button
                    type="button"
                    onClick={() => setTheater(t => !t)}
                    title={theater ? 'Revenir à la taille normale' : 'Agrandir le lecteur sur la page'}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      border: '1px solid ' + (theater ? 'var(--color-primary, #e27228)' : 'var(--color-border, #e2e8f0)'),
                      background: theater ? 'var(--color-primary, #e27228)' : 'transparent',
                      color: theater ? '#fff' : 'var(--color-text)', fontFamily: 'inherit',
                    }}
                  >{theater ? '⤡ Réduire' : '⤢ Agrandir'}</button>
                  <button
                    type="button"
                    onClick={goFullscreen}
                    title="Plein écran sur tout le moniteur (Échap pour sortir)"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      border: '1px solid var(--color-border, #e2e8f0)', background: 'transparent',
                      color: 'var(--color-text)', fontFamily: 'inherit',
                    }}
                  >⛶ Plein écran</button>
                </div>

                {/* Partage réseaux */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {shareTargets(shareUrl, current.title).map(t => (
                    <a key={t.name} href={t.href} target="_blank" rel="noopener noreferrer"
                      style={{
                        fontSize: 12.5, fontWeight: 600, padding: '6px 12px', borderRadius: 999,
                        border: '1px solid var(--color-border, #e2e8f0)', color: 'var(--color-text)',
                        textDecoration: 'none', background: 'var(--color-bg-alt, #f8fafc)',
                      }}>{t.name}</a>
                  ))}
                </div>

                {current.description && (
                  <p style={{
                    margin: 0, fontSize: 14.5, lineHeight: 1.7,
                    color: 'var(--color-text-muted)', whiteSpace: 'pre-line',
                    padding: '12px 14px', background: 'var(--color-bg-alt, #f8fafc)',
                    borderRadius: 10,
                  }}>{current.description}</p>
                )}
              </div>

              {/* ── Colonne droite : les autres capsules (liste verticale) ── */}
              <div>
                <div style={{
                  fontSize: 13, fontWeight: 700, color: 'var(--color-text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12,
                }}>À regarder ensuite</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {others.map(v => {
                    const src = parseVideoSource(v.url);
                    const thumb = autoThumbnail(src, v);
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => openVideo(v.id)}
                        style={{
                          display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10,
                          textAlign: 'left', background: 'transparent', border: 'none',
                          padding: 0, cursor: 'pointer', fontFamily: 'inherit', alignItems: 'start',
                        }}
                        aria-label={`Lire la vidéo : ${v.title || ''}`}
                      >
                        <span style={{
                          position: 'relative', display: 'block', aspectRatio: '16 / 9',
                          borderRadius: 0, overflow: 'hidden',
                          background: 'linear-gradient(135deg, var(--color-secondary, #1a1a1a), #334155)',
                        }}>
                          {thumb && (
                            <img src={thumb} alt={v.title || 'Vidéo'} loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: 'scale(1.06)' }} />
                          )}
                          {v.duration && (
                            <span style={{
                              position: 'absolute', right: 5, bottom: 5,
                              background: 'rgba(0,0,0,0.8)', color: 'white',
                              fontSize: 11, fontWeight: 600, padding: '1px 5px', borderRadius: 4,
                            }}>{v.duration}</span>
                          )}
                        </span>
                        <span>
                          <span style={{
                            display: 'block', fontWeight: 600, fontSize: 13.5,
                            color: 'var(--color-text)', lineHeight: 1.35, marginBottom: 4,
                          }}>{v.title || 'Sans titre'}</span>
                          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            {statLine(v.id)}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                  {!others.length && (
                    <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                      D'autres capsules arrivent bientôt.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ══ MODE GRILLE (aucune video selectionnee) ══ */
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${columns >= 4 ? 240 : columns === 3 ? 300 : 380}px), 1fr))`,
            gap: 24,
            alignItems: 'start', // toutes les cartes alignées en HAUT
          }}>
            {videos.map(v => {
              const src = parseVideoSource(v.url);
              const thumb = autoThumbnail(src, v);
              const st = stats[v.id] || {};
              return (
                <button
                  key={v.id}
                  type="button"
                  className="lgbx-card"
                  onClick={() => openVideo(v.id)}
                  style={{
                    display: 'block', width: '100%',
                    textAlign: 'left', border: 'none',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                  aria-label={`Lire la vidéo : ${v.title || ''}`}
                >
                  <div style={{
                    position: 'relative', aspectRatio: '16 / 9', borderRadius: 0,
                    overflow: 'hidden', background: 'linear-gradient(135deg, var(--color-secondary, #1a1a1a), #334155)',
                  }}>
                    {thumb ? (
                      /* scale(1.06) : les vignettes YouTube (hqdefault) sont en
                         4:3 avec des bandes noires INCRUSTÉES — le sur-recadrage
                         élimine le liseré noir résiduel en haut/bas. */
                      <img src={thumb} alt={v.title || 'Vidéo'} loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: 'scale(1.06)' }} />
                    ) : null}
                    <span style={{
                      position: 'absolute', inset: 0, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{
                        width: 58, height: 58, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.55)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                          <path d="M8 5.14v13.72L19 12 8 5.14z" />
                        </svg>
                      </span>
                    </span>
                    {v.duration && (
                      <span style={{
                        position: 'absolute', right: 8, bottom: 8,
                        background: 'rgba(0,0,0,0.8)', color: 'white',
                        fontSize: 12, fontWeight: 600, padding: '2px 7px', borderRadius: 5,
                      }}>{v.duration}</span>
                    )}
                  </div>
                  <div style={{ padding: '12px 4px 0' }}>
                    <div style={{
                      fontWeight: 700, fontSize: 15.5, color: 'var(--color-text)',
                      lineHeight: 1.35, marginBottom: 6,
                    }}>{v.title || 'Sans titre'}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>
                      {statLine(v.id)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
